import { isEmpty } from 'lodash';
import { Button, notification } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormikErrors, FormikProps, FormikProvider, setNestedObjectValues } from 'formik';
import { Outlet, useLocation, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import styled from 'styled-components';
import Notification from 'components/ui/base/notification/Notification';
import useDeclarationFooter from 'hooks/useDeclarationFooter';
import useDeclarations, { UpdateDeclarationFunction } from 'hooks/useDeclarations';
import useProducts from 'hooks/useProducts';
import { DeclarationFooterType } from 'store/declaration-footer/declaration-footer';
import { Declaration, DeclarationHistory } from 'store/declarations/declaration';
import { IrelandExportDeclaration } from 'store/declarations/ireland/export-declaration';
import { IrelandH7ImportDeclaration } from 'store/declarations/ireland/h7-import-declaration';
import { IrelandImportDeclaration } from 'store/declarations/ireland/import-declaration';
import { canEnsBeAmended, isDeclarationAmendable } from 'views/declarations/utils/declaration-utils';
import {
    removeEmptyObjectsFromDeclarationArrays,
    removeEmptyObjects,
    trimWhiteSpaces,
    FormAction,
} from 'views/declarations/utils/form-utils';
import AmendmentFooter from './AmendmentFooter';
import AmendmentReasonModal from './AmendmentReasonModal';
import { Box44Context } from '../../common/box44/Box44';
import { FooterRow, SubmitDiv } from 'views/declarations/footer/Footer.styles';
import axiosClient from 'config/axios';
import { ListPayload, SuccessResponse } from 'core/http/response';
import config from 'config/config';
import CdsAmendmentModal, { AmendmentRecord } from './components/cds/CdsAmendmentModal';
import useRequest from 'hooks/useRequest';
import {
    prepareAesAmendment,
    prepareCdsAmendment,
    prepareGmrAmendment,
    submitCdsAmendmentRequest,
} from 'store/declarations/client';
import useGlobalOverlay from 'hooks/useGlobalOverlay';
import CdsExportDeclarationUtils from 'views/declarations/uk/export/utils';
import useDeclarationFormErrors from 'hooks/useDeclarationFormErrors';
import ValidationErrorContainer from 'views/declarations/common/ValidationErrorContainer/ValidationErrorContainer';
import useDeclarationValidation from 'hooks/useDeclarationValidation';
import useGetDeclarationMapValues from '../../../../hooks/useGetDeclarationMapValues';
import { box44PathAndFieldNames } from '../../common/box44/mappers';
import useFormUtils from '../../../../hooks/useFormUtils';
import InvalidateModalButton from '../../common/invalidate-modal/InvalidateModal';
import ConsultEnsModalButton from '../../common/consult-ens/ConsultEnsModalButton';
import CancelDeclarationModalButton from '../../common/CancelDeclarationModal';
import PresentationNotificationModalButton from '../../common/PresentationNotificationModal';
import GeneralizedAmendmentModal from './GeneralizedAmendmentModal';
import ukGvmsRecordParser from '../../uk/gvms/validation/ukGvmsRecordParser';
import irelandExportValidationErrorsParser from '../../ireland/export/validation/IrelandExportValidationErrorsParser';
import useCodelists from 'hooks/useCodelists';
import { getDeclarationType } from 'views/declarations/utils/declaration-utils';
import { getDeclarationUrl } from 'views/declarations/utils/getDeclarationUrl';
import { DeclarationInternalType } from 'store/declarations/enums/common/declaration-internal-type';
import { DeclarationCountry } from 'store/declarations/enums/common/declaration-country';
import useHandleErrors from '../declaration-form/utils/useHandleErrors';
import useHandleUnsavedChanges from '../declaration-form/utils/useHandleUnsavedChanges';
import useHouseGoodsItems from '../declaration-form/utils/useHouseGoodsItems';
import { StyledHeader, StyledLayout } from '../declaration-form/Form.styles';
import DeclarationFormContent from '../declaration-form/DeclarationFormContent';
import { FormTabs } from '../declaration-form/DeclarationFormTab';
import { ProductsTypes } from 'store/products/reducer';
import FormNavigation from '../declaration-form/components/FormNavigation';
import { getFormActiveTabKey } from '../declaration-form/utils/getFormActiveTabKey';
import useProductNavigation from 'hooks/useProductNavigation';

export const FooterButton = styled(Button)`
    padding-inline: 3rem;
    height: 4.5rem;
`;

type SubmitDeclaration = IrelandImportDeclaration | IrelandExportDeclaration | IrelandH7ImportDeclaration;

const FormViewOnly = () => {
    const location = useLocation();
    const { declaration, productsFormik, formik, houseConsignmentFormik } = useViewOnly();
    const { declarationFormType, countryLowerCase: country, internalType, formType } = useFormUtils();
    const { showGlobalOverlay, hideGlobalOverlay } = useGlobalOverlay();
    const { declarationId, manifestId, jobId, consignmentId, productId } = useParams();
    const { activeProductParams, updateActiveProductParams, refetchProducts, fetchProductPage } =
        useProductNavigation();
    const { houseGoodsItems, refetchHouseGoodsItems } = useHouseGoodsItems(declarationId, declaration);

    const baseUrl = getDeclarationUrl({ manifestId, jobId, declarationId });
    const formViewOnlyLocation = `${baseUrl}/view-only`;
    const isArchived = declaration?.archived || false;

    const [selectedFormTab, setSelectedFormTab] = useState(getFormActiveTabKey(location, formViewOnlyLocation));

    const navigate = useNavigate();
    const { setDeclarationFooterType } = useDeclarationFooter();
    const { products, listDeclarationProducts } = useProducts();
    const { setFormDeclarationErrors, clearFormDeclarationErrors } = useDeclarationFormErrors();
    const declarationViewMapValues = useGetDeclarationMapValues();
    const [triedToSubmit, setTriedToSubmit] = useState(false);

    const [isAmendmentActive, setIsAmendmentActive] = useState<boolean>(false);

    // ICS2 has its own amendability rules and its own submit path, so it is separated from the
    // Ireland/GVMS-shaped isDeclarationAmendable rather than being added to it.
    const isIcs2Ens = Boolean(declaration?.ics2ENSDeclaration);
    const canAmendDeclaration = isIcs2Ens
        ? canEnsBeAmended(declaration)
        : Boolean(declaration && isDeclarationAmendable(declaration));

    const [isAmendmentReasonModalOpen, setIsAmendmentReasonModalOpen] = useState<boolean>(false);
    const [isCdsAmendmentModalOpen, setIsCdsAmendmentModalOpen] = useState<boolean>(false);
    const openCdsAmendmentModal = () => setIsCdsAmendmentModalOpen(true);
    const closeCdsAmendmentModal = () => setIsCdsAmendmentModalOpen(false);

    const [isGeneralizedAmendmentModalOpen, setIsGeneralizedAmendmentModalOpen] = useState<boolean>(false);
    const openGeneralizedAmendmentModal = () => setIsGeneralizedAmendmentModalOpen(true);
    const closeGeneralizedAmendmentModal = () => setIsGeneralizedAmendmentModalOpen(false);

    const [errorModalOpen, setErrorModalOpen] = useState(false);
    const openErrorModal = () => setErrorModalOpen(true);
    const closeErrorModal = () => setErrorModalOpen(false);

    const declarations = useDeclarations();
    const { getCodeLists } = useCodelists();
    const declarationType = getDeclarationType(declaration);
    const customerId = declaration?.individual?.customerId;

    useEffect(() => {
        setSelectedFormTab(getFormActiveTabKey(location, formViewOnlyLocation));
    }, [location, declarationId, formViewOnlyLocation]);

    useEffect(() => {
        if (
            !declarationId ||
            declarations?.declaration?.gvmsDeclaration ||
            declarations?.declaration?.preBoardingNotification
        )
            return;
        listDeclarationProducts({ declarationId, country, formType, internalType });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [declarationId, declarations?.declaration?.gvmsDeclaration]);

    useEffect(() => {
        if (selectedFormTab === FormTabs.MASTER_DETAILS) {
            setDeclarationFooterType(DeclarationFooterType.MASTER_DETAILS);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedFormTab]);

    useEffect(() => {
        getCodeLists(country, declarationType, customerId);
    }, [getCodeLists, country, declarationType, customerId]);

    const inViewOnlyDeclarationProductsTable = location.pathname.endsWith('view-only/products');

    const viewOnly = useMemo(() => {
        if (inViewOnlyDeclarationProductsTable) {
            return true;
        }
        return !isAmendmentActive;
    }, [isAmendmentActive, inViewOnlyDeclarationProductsTable]);

    const handleSubmitForm = async (values: SubmitDeclaration) => {
        let form = removeEmptyObjects(
            removeEmptyObjectsFromDeclarationArrays(
                trimWhiteSpaces(values, {
                    emptyStringToNull: true,
                })
            )
        );
        if (declarationViewMapValues?.transformData?.declaration?.forServer !== undefined) {
            if (declarationViewMapValues?.payloadPath) {
                form = {
                    ...declaration?.[declarationViewMapValues?.declarationType],
                    [declarationViewMapValues?.payloadPath]: form,
                };
            }
            form = declarationViewMapValues.transformData.declaration.forServer(form);
        }
        const data = { ...declaration, [declarationViewMapValues?.declarationType as any]: form };
        if (!(declarations.declaration?.customerId! && declarationId)) return;
        showGlobalOverlay({ type: 'LoadingOverlay', payload: { message: 'Saving declaration...' } });
        return (declarations[declarationViewMapValues?.updateDeclarationFuncName] as UpdateDeclarationFunction)(
            declarations.declaration?.customerId,
            declarationId,
            data,
            false
        ).finally(() => {
            hideGlobalOverlay();
        });
    };

    const { setFormAction } = useDeclarationValidation({
        formAction: null,
    });

    /** Marks the form and opens the error modal when invalid; returns whether it may be submitted. */
    const validateBeforeSubmit = useCallback(async () => {
        const errors = await formik.validateForm();

        if (isEmpty(errors)) {
            clearFormDeclarationErrors();
            return true;
        }

        formik.setTouched(setNestedObjectValues(errors, true));
        if (declarationViewMapValues?.parseForm) setFormDeclarationErrors(declarationViewMapValues.parseForm(errors));
        openErrorModal();
        return false;
    }, [formik, declarationViewMapValues, setFormDeclarationErrors, clearFormDeclarationErrors]);

    /** The house consignment form is separate, so the open one has to be folded back in by hand. */
    const withEditedHouseConsignment = useCallback(
        (values: any) => {
            const houseConsignments = values?.consignmentMasterLevel?.consignmentHouseLevel;
            if (!consignmentId || !houseConsignments?.length) return values;

            return {
                ...values,
                consignmentMasterLevel: {
                    ...values.consignmentMasterLevel,
                    consignmentHouseLevel: houseConsignments.map((houseConsignment: any) =>
                        houseConsignment.id === consignmentId
                            ? {
                                  ...houseConsignment,
                                  ...houseConsignmentFormik?.values,
                                  id: houseConsignment.id,
                                  goodsItem: houseConsignment.goodsItem,
                              }
                            : houseConsignment
                    ),
                },
            };
        },
        [consignmentId, houseConsignmentFormik]
    );

    /**
     * Submits an ENS amendment without persisting the form first: the backend compares it against the
     * stored filing to catch changes to non-amendable fields, and saving would overwrite that baseline.
     */
    const submitIcs2Amendment = useCallback(async () => {
        if (!(await validateBeforeSubmit()) || !declarationId || !declaration) return;

        showGlobalOverlay({ type: 'LoadingOverlay', payload: { message: 'Submitting amendment...' } });
        try {
            // The form holds the payload, not the declaration wrapping it - assigning it over the
            // wrapper would drop the messageType the backend resolves its service by.
            const payload = removeEmptyObjects(
                removeEmptyObjectsFromDeclarationArrays(
                    trimWhiteSpaces(withEditedHouseConsignment(formik.values), { emptyStringToNull: true })
                )
            );

            await declarations.amendEnsIcs2Declaration(declarationId, {
                ...declaration,
                ics2ENSDeclaration: { ...declaration.ics2ENSDeclaration, payload },
            } as Declaration);

            setIsAmendmentActive(false);
            notification.success({ message: 'Amendment submitted' });
            await declarations.getDeclaration(declarationId);
        } catch (error: any) {
            notification.error({ message: error?.response?.data?.message ?? 'Failed to submit the amendment' });
        } finally {
            hideGlobalOverlay();
        }
    }, [
        formik,
        declaration,
        declarationId,
        declarations,
        validateBeforeSubmit,
        withEditedHouseConsignment,
        showGlobalOverlay,
        hideGlobalOverlay,
    ]);

    const formikForProvider = useMemo(
        () => (!location.pathname.includes('products') ? formik : productsFormik),
        [formik, location.pathname, productsFormik]
    );

    const { data: amendments, doRequest: doPrepareCdsExportAmendment } = useRequest(prepareCdsAmendment);
    const { data: generalizedAmendmentData, doRequest: doPrepareGeneralizedAmendment } = useRequest(
        declaration?.gvmsDeclaration ? prepareGmrAmendment : prepareAesAmendment
    );

    const nonAmendmentFooter = useMemo(() => {
        if (isAmendmentActive || isArchived) return null;

        const canSendArrivalNotification =
            declaration?.cdsDeclaration && ['REGISTERED', 'RISKED'].includes(declaration?.status);
        const sendArrivalNotification = () => {
            if (!canSendArrivalNotification) throw new Error('Not allowed to send arrival notification');

            axiosClient
                .post<SuccessResponse<any>>(
                    `${config.declarationsUrl}/uk/declarations/${declarationId}/arrivalNotification`
                )
                .then(() => {
                    notification.success({ message: 'Arrival Notification sent' });
                    declarations.getDeclaration(declarationId!);
                })
                .catch(() => {
                    notification.error({ message: 'Error sending Arrival Notification' });
                });
        };

        return (
            <SubmitDiv>
                <FooterRow justify="end" align="middle" padding="1.2rem 2.4rem 1.2rem 2.4rem">
                    <div style={{ display: 'grid', gridAutoFlow: 'column', gap: '1rem' }}>
                        {canSendArrivalNotification && (
                            <FooterButton onClick={sendArrivalNotification}>Send Arrival Notification</FooterButton>
                        )}

                        <PresentationNotificationModalButton
                            declaration={declaration}
                            declarationId={declarationId}
                            importPresentationNotificationRequest={declarations.importPresentationNotification}
                        />

                        {/* Hide the button for now */}
                        {/* <ConsultEnsModalButton declaration={declaration} /> */}

                        <InvalidateModalButton declaration={declaration} />

                        {canAmendDeclaration && (
                            <FooterButton onClick={() => setIsAmendmentActive(true)}>
                                {isIcs2Ens ? 'Amend ENS' : 'Amend Declaration'}
                            </FooterButton>
                        )}
                        <CancelDeclarationModalButton
                            declaration={declaration}
                            cancelCdsDeclarationRequest={declarations.cancelCdsDeclaration}
                        />
                    </div>
                </FooterRow>
            </SubmitDiv>
        );
    }, [isAmendmentActive, isArchived, declaration, declarationId, declarations, canAmendDeclaration, isIcs2Ens]);

    useEffect(() => {
        if (selectedFormTab === FormTabs.MASTER_DETAILS) {
            setDeclarationFooterType(DeclarationFooterType.MASTER_DETAILS);
        } else {
            setDeclarationFooterType(DeclarationFooterType.PRODUCTS);
        }
    }, [selectedFormTab, setDeclarationFooterType]);

    const [declarationSaved, setDeclarationSaved] = useState(false);

    useHandleErrors({
        formik,
        productsFormik,
        validationErrorsParser: declarationViewMapValues?.parseForm,
        triedToSubmit,
    });
    useHandleUnsavedChanges({
        declarationSaved,
        setDeclarationSaved,
        formik,
        productsFormik,
    });
    const hasAdditionalInformationFields =
        declaration?.declarationInternalType === DeclarationInternalType.IMPORT &&
        country === DeclarationCountry.IRELAND;

    return (
        <>
            <StyledHeader>
                {declarationFormType && (
                    <FormikProvider value={formikForProvider}>
                        <Box44Context.Provider
                            value={{
                                documentTypeName: box44PathAndFieldNames[declarationFormType]?.documentTypeName,
                                documentIdentifierName:
                                    box44PathAndFieldNames[declarationFormType]?.documentIdentifierName,
                                path: !location.pathname.includes('products')
                                    ? box44PathAndFieldNames[declarationFormType]?.masterPath
                                    : box44PathAndFieldNames[declarationFormType]?.productPath,
                                hasAdditionalInfo: hasAdditionalInformationFields,
                            }}
                        >
                            <FormNavigation
                                viewOnly
                                products={products as ListPayload<ProductsTypes>}
                                houseConsignments={formik?.values?.consignmentMasterLevel?.consignmentHouseLevel ?? []}
                                houseGoodsItems={houseGoodsItems}
                                numItems={products?.total || 0}
                                selectedFormTab={selectedFormTab}
                                amendment={isAmendmentActive}
                                refetchProducts={refetchProducts}
                                fetchProductPage={fetchProductPage}
                                activeProductParams={activeProductParams}
                            />
                        </Box44Context.Provider>
                    </FormikProvider>
                )}
            </StyledHeader>
            <StyledLayout>
                <DeclarationFormContent>
                    <Outlet
                        context={{
                            formik,
                            viewOnly,
                            amendment: isAmendmentActive,
                            productsFormik,
                            houseConsignmentFormik,
                            houseGoodsItems,
                            refetchHouseGoodsItems,
                            onEdit: (id: string) => {
                                // An ENS has no products list - its master items have their own tab
                                // and route, so editing one from there has to stay under it. Sending
                                // it to /products leaves the item form without its list context.
                                const itemsSegment =
                                    selectedFormTab === FormTabs.MASTER_ITEMS
                                        ? FormTabs.MASTER_ITEMS
                                        : FormTabs.PRODUCTS;
                                navigate(`${baseUrl}/view-only/${itemsSegment}/${id}`);
                                setDeclarationFooterType(DeclarationFooterType.PRODUCTS);
                            },
                            setActiveProductParams: updateActiveProductParams,
                        }}
                    />
                </DeclarationFormContent>
            </StyledLayout>
            {!isAmendmentActive ? (
                <>{nonAmendmentFooter}</>
            ) : (
                <>
                    {canAmendDeclaration && (
                        <AmendmentFooter
                            // Saving would overwrite the filing the backend compares the IE3Axx against.
                            hideSaveDraft={isIcs2Ens}
                            // The footer type follows the tab, so the item and consignment lists get the
                            // product actions too. An ENS only wants them on an open goods item.
                            alwaysShowDeclarationActions={isIcs2Ens && !productId}
                            hideBackToProducts={isIcs2Ens}
                            cancelSaveProduct={() => {
                                navigate(`${baseUrl}/view-only/products`);
                                setDeclarationFooterType(DeclarationFooterType.MASTER_DETAILS);
                            }}
                            saveProduct={async () => {
                                showGlobalOverlay({
                                    type: 'LoadingOverlay',
                                    payload: { message: 'Saving product...' },
                                });
                                setTriedToSubmit(false);
                                setFormAction(FormAction.PRODUCT);
                            }}
                            cancelAmendment={() => {
                                setIsAmendmentActive(false);
                                // An ICS2 amendment is never persisted before submission, so there is
                                // nothing to roll back - and cancelAmendment reverts the declaration to
                                // the last submitted version, which would discard state we never touched.
                                if (isIcs2Ens || !declaration) return;
                                declarations.cancelAmendment(declaration);
                            }}
                            saveDraft={async () => {
                                showGlobalOverlay({
                                    type: 'LoadingOverlay',
                                });
                                setTriedToSubmit(false);
                                setFormAction(FormAction.DRAFT);
                            }}
                            submitAmendment={async () => {
                                setTriedToSubmit(true);

                                if (isIcs2Ens) {
                                    await submitIcs2Amendment();
                                    return;
                                }

                                showGlobalOverlay({
                                    type: 'LoadingOverlay',
                                    payload: { message: 'Saving declaration...' },
                                });
                                let data = { ...formik.values };
                                const response = await handleSubmitForm(data);

                                if (!(await validateBeforeSubmit())) {
                                    hideGlobalOverlay();
                                    return;
                                }

                                if (!response?.id) {
                                    console.error('No response on form submission');
                                    hideGlobalOverlay();
                                    return;
                                }

                                if (declaration?.cdsDeclaration) {
                                    showGlobalOverlay({
                                        type: 'LoadingOverlay',
                                        payload: { message: 'Preparing amendment...' },
                                    });
                                    const items = await doPrepareCdsExportAmendment(declarationId!);
                                    hideGlobalOverlay();
                                    if (isEmpty(items?.items)) {
                                        notification.warning({ message: 'No changes have been made.' });
                                        return;
                                    }
                                    openCdsAmendmentModal();
                                    return;
                                }

                                hideGlobalOverlay();

                                if (declaration?.irelandImportDeclaration || declaration?.entrySummaryDeclaration) {
                                    setIsAmendmentReasonModalOpen(true);

                                    return;
                                }

                                if (declaration?.ieExportDeclaration || declaration?.gvmsDeclaration) {
                                    showGlobalOverlay({
                                        type: 'LoadingOverlay',
                                        payload: { message: 'Preparing amendment...' },
                                    });
                                    const response = await doPrepareGeneralizedAmendment(declarationId!);
                                    hideGlobalOverlay();
                                    if (isEmpty(response)) {
                                        notification.warning({ message: 'No changes have been made.' });
                                        return;
                                    }

                                    openGeneralizedAmendmentModal();
                                    return;
                                }
                            }}
                        />
                    )}
                </>
            )}
            <AmendmentReasonModal
                open={isAmendmentReasonModalOpen}
                onCancel={() => {
                    setIsAmendmentReasonModalOpen(false);
                }}
                onSubmit={async ({ detailsAmended }) => {
                    if (!declarationId) return;
                    const createAmendment =
                        declaration?.irelandImportDeclaration || declaration?.irelandH7ImportDeclaration
                            ? declarations.createAmendmentIrelandImportDeclaration
                            : declaration?.entrySummaryDeclaration
                            ? declarations.createAmendmentIrelandEntrySummaryDeclaration
                            : undefined;

                    const response: DeclarationHistory | undefined = await createAmendment?.(
                        detailsAmended,
                        declarationId
                    );

                    if (response?.id) {
                        setIsAmendmentReasonModalOpen(false);

                        Notification({
                            type: 'success',
                            messageTitle: 'New Amendment',
                            description: 'A new amendment was successfully created.',
                            placement: 'top',
                        });
                    }
                }}
            />
            <CdsAmendmentModal
                amendments={amendments?.items}
                open={isCdsAmendmentModalOpen}
                onCancel={closeCdsAmendmentModal}
                submitAmendmentRequest={async (value: AmendmentRecord[]) => {
                    if (!declarationId || !declaration?.mrn || !declaration?.cdsDeclaration?.lrn) {
                        notification.error({ message: 'Missing information for submitting amendment request' });
                        return Promise.reject();
                    }

                    showGlobalOverlay({ type: 'LoadingOverlay', payload: { message: 'Submitting amendment...' } });
                    return submitCdsAmendmentRequest(declarationId!, {
                        items: value,
                        declarationId: declarationId,
                        lrn: declaration.cdsDeclaration?.lrn,
                        mrn: declaration.mrn,
                    })
                        .then(() => {
                            setIsAmendmentActive(false);
                            notification.success({ message: 'Amendment submitted' });
                        })
                        .catch(() => {
                            notification.error({ message: 'Could not submit amendment' });
                        });
                }}
                onChangesReverted={async () => {
                    if (!declarationId) return true;
                    showGlobalOverlay({ type: 'LoadingOverlay', payload: { message: 'Validating declaration...' } });
                    const declaration = await declarations.getDeclaration(declarationId);
                    const errors = (await formik.validateForm(
                        CdsExportDeclarationUtils.transformForClient(declaration.cdsDeclaration).cdsDeclarationPayload
                    )) as FormikErrors<any>;
                    if (!isEmpty(errors)) {
                        formik.setTouched(setNestedObjectValues(errors, true));
                        if (declarationViewMapValues?.parseForm)
                            setFormDeclarationErrors(declarationViewMapValues?.parseForm(errors));
                        openErrorModal();
                        closeCdsAmendmentModal();
                        return true;
                    }
                    clearFormDeclarationErrors();
                    return false;
                }}
            />
            <GeneralizedAmendmentModal
                amendments={generalizedAmendmentData}
                open={isGeneralizedAmendmentModalOpen}
                onCancel={closeGeneralizedAmendmentModal}
                fieldNameParams={{
                    productPath: declaration?.gvmsDeclaration ? null : 'goodsShipment.goodsItem',
                    formParser: declaration?.gvmsDeclaration ? ukGvmsRecordParser : irelandExportValidationErrorsParser,
                }}
                submitAmendmentRequest={async () => {
                    if (!(declaration && declaration?.id)) {
                        notification.error({ message: 'Missing information for submitting amendment request' });
                        return Promise.reject();
                    }

                    showGlobalOverlay({ type: 'LoadingOverlay', payload: { message: 'Submitting amendment...' } });

                    await declarations
                        .createAmendment(declaration, declaration.id)
                        ?.then(() => {
                            setIsAmendmentActive(false);
                            notification.success({ message: 'Amendment submitted' });
                        })
                        .catch(() => {
                            notification.error({ message: 'Could not submit amendment' });
                        })
                        .finally(() => closeGeneralizedAmendmentModal());
                }}
            />
            <ValidationErrorContainer isOpen={errorModalOpen} close={closeErrorModal} />
        </>
    );
};
export default FormViewOnly;

function useViewOnly() {
    return useOutletContext<{
        formik: FormikProps<any>;
        productsFormik: FormikProps<any>;
        houseConsignmentFormik?: FormikProps<any>;
        declaration: Declaration | undefined;
        activeTabKey: string;
    }>();
}
